import { cn } from '../../../../lib/utils';
import LoadingSpinner from '../../feedback-and-status/loading-spinner/LoadingSpinner';

export type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large';
export type ButtonHierarchy =
  | 'primary'
  | 'primary-variant'
  | 'tertiary'
  | 'destructive';
export type ButtonState = 'default' | 'disabled' | 'loading';

interface ButtonProps extends React.ComponentProps<'button'> {
  /** 버튼 크기 */
  size?: ButtonSize;
  /** 버튼 계층 */
  hierarchy?: ButtonHierarchy;
  /** 버튼 상태 */
  state?: ButtonState;
  /** 버튼 앞쪽 아이콘 */
  leadingNode?: React.ReactNode;
  /** 버튼 앞쪽 아이콘 클릭 이벤트 */
  leadingOnClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** 버튼 뒤쪽 아이콘 */
  trailingNode?: React.ReactNode;
  /** 버튼 뒤쪽 아이콘 클릭 이벤트 */
  trailingOnClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  /** 버튼 안의 텍스트 or ReactNode */
  children: React.ReactNode;
}

export default function Button({
  size = 'medium',
  hierarchy = 'primary',
  state = 'default',
  leadingNode,
  leadingOnClick,
  trailingNode,
  trailingOnClick,
  children,
  className,
  ...props
}: ButtonProps) {
  const getButtonPaddingClass = (size: ButtonSize) => {
    switch (size) {
      case 'xsmall':
        return 'p-2xs';
      case 'small':
        return 'px-sm py-2xs';
      case 'medium':
        return 'px-xs py-2xs';
      case 'large':
        return 'py-xs px-sm';
    }
  };

  const getButtonSizeClass = (size: ButtonSize) => {
    switch (size) {
      case 'xsmall':
        return 'w-full min-h-[28px] rounded-2xs';
      case 'small':
        return 'w-full min-h-[36px] rounded-xs';
      case 'medium':
        return 'w-full min-h-[44px] rounded-sm';
      case 'large':
        return 'w-full min-h-[52px] rounded-sm';
    }
  };

  const getButtonGapClass = (size: ButtonSize) => {
    switch (size) {
      case 'xsmall':
        return 'gap-5xs';
      case 'small':
        return 'gap-5xs';
      case 'medium':
        return 'gap-4xs';
      case 'large':
        return 'gap-4xs';
    }
  };

  const getButtonTextSizeClass = (size: ButtonSize) => {
    switch (size) {
      case 'xsmall':
        return 'text-comp-accent-xsmall';
      case 'small':
        return 'text-comp-accent-small';
      case 'medium':
        return 'text-comp-accent-medium';
      case 'large':
        return 'text-comp-accent-large';
    }
  };

  const getButtonTextColorClass = (hierarchy: ButtonHierarchy) => {
    switch (hierarchy) {
      case 'primary':
        return 'text-comp-button-primary-text-default';
      case 'primary-variant':
        return 'text-comp-button-primary-variant-text-default';
      case 'tertiary':
        return 'text-comp-button-tertiary-text-default';
      case 'destructive':
        return 'text-comp-button-destructive-text-default';
    }
  };

  /* Icon Style */
  const getIconSizeClass = (size: ButtonSize) => {
    switch (size) {
      case 'xsmall':
        return '[&>svg]:w-[12px] [&>svg]:h-[12px]';
      case 'small':
        return '[&>svg]:w-[16px] [&>svg]:h-[16px]';
      case 'medium':
        return '[&>svg]:w-[20px] [&>svg]:h-[20px]';
      case 'large':
        return '[&>svg]:w-[24px] [&>svg]:h-[24px]';
    }
  };

  return (
    <button
      className={cn(
        'global-button-icon-state relative flex w-full items-center justify-center gap-2xs transition-all',
        getButtonSizeClass(size),
        getButtonGapClass(size),
        getButtonPaddingClass(size),
        className,
      )}
      data-custom-hierarchy={hierarchy}
      data-custom-state={state}
      disabled={state === 'disabled'}
      {...props}
    >
      {leadingNode && (
        <div
          className={cn(getIconSizeClass(size))}
          onClick={leadingOnClick}
        >
          {leadingNode}
        </div>
      )}
      {children && (
        <div
          className={cn(
            'button-text-class text- sys-cont-none-light',
            // leadingNode나 trailingNode가 있으면 w-auto, 없으면 w-full
            leadingNode || trailingNode ? 'w-auto' : 'w-full',
            getButtonTextColorClass(hierarchy),
          )}
        >
          <div className={cn('transition-all', getButtonTextSizeClass(size))}>
            {children}
          </div>
        </div>
      )}
      {trailingNode && (
        <div
          className={cn(getIconSizeClass(size))}
          onClick={trailingOnClick}
        >
          {trailingNode}
        </div>
      )}
      <div className="button-loading-spinner">
        <LoadingSpinner size={size} />
      </div>
    </button>
  );
}
